/**
 * Réglages du Live School du dimanche d'un programme (admin, migration 0329) :
 * live activé, SKILLCARD à gagner (légendaire hors pack), taille du podium.
 * Sans lot choisi : une SKILLCARD du pack du moment est mise en jeu (0330).
 */

import { Radio } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  SkillcardPickerField,
  toSkillcardPickerItem,
} from '@/features/skillcards/skillcard-picker';
import {
  LIVE_PRIZE_TOP_N_CHOICES,
  useLivePrizeCandidates,
  useProgrammeLiveSettings,
  useSaveProgrammeLive,
} from '@/lib/curriculum/live-prize';

export function ProgrammeLivePanel({ programmeId }: { programmeId: string }) {
  const settings = useProgrammeLiveSettings(programmeId);
  const candidates = useLivePrizeCandidates();
  const save = useSaveProgrammeLive(programmeId);
  const s = settings.data;
  const items = (candidates.data ?? []).map(toSkillcardPickerItem);

  return (
    <section
      className="border-ink-100 ocean:bg-white/5 space-y-3 rounded-2xl border bg-white p-4"
      aria-label="Live School du dimanche"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h3 className="text-ink-900 flex items-center gap-2 text-sm font-bold">
            <Radio className="text-primary-600 h-4 w-4" aria-hidden /> Live School du dimanche
          </h3>
          <p className="text-ink-500 text-xs">
            Un live de 10 min chaque dimanche dès 10 h (questions des SKILLCARDS de ce programme).
          </p>
        </div>
        {s && (
          <button
            type="button"
            role="switch"
            aria-checked={s.live_enabled}
            aria-label="Live activé"
            disabled={save.isPending}
            onClick={() => save.mutate({ live_enabled: !s.live_enabled })}
            className={cn(
              'relative inline-flex h-7 w-12 shrink-0 rounded-full border-2 border-transparent transition-colors',
              s.live_enabled ? 'bg-primary-500' : 'bg-ink-200',
            )}
          >
            <span
              className={cn(
                'inline-block h-6 w-6 rounded-full bg-white shadow transition-transform',
                s.live_enabled ? 'translate-x-5' : 'translate-x-0',
              )}
            />
          </button>
        )}
      </div>

      {settings.isLoading ? (
        <div className="bg-ink-100 h-16 animate-pulse rounded-xl" />
      ) : !s ? (
        <p className="text-ink-500 text-xs">Réglages indisponibles.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <SkillcardPickerField
            label="SKILLCARD à gagner (légendaire, hors pack)"
            items={items}
            value={s.live_prize_card_id}
            onChange={(id) => save.mutate({ live_prize_card_id: id })}
            noneLabel="Par défaut : une SKILLCARD du pack du moment"
            title="Lot du Live School"
            placeholder={
              candidates.isLoading
                ? 'Chargement…'
                : items.length === 0
                  ? 'Aucune SKILLCARD légendaire hors pack'
                  : 'Choisir le lot…'
            }
            disabled={save.isPending}
          />
          <label className="block space-y-1">
            <span className="text-ink-700 text-sm font-semibold">Gagnants</span>
            <select
              className="border-ink-200 text-ink-900 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 text-sm"
              value={s.live_prize_top_n}
              disabled={save.isPending}
              onChange={(e) => save.mutate({ live_prize_top_n: Number(e.target.value) })}
            >
              {LIVE_PRIZE_TOP_N_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n === 1 ? 'Le 1er' : `Top ${n}`}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {!candidates.isLoading && (
        <p className="text-ink-500 text-xs">
          Sans lot choisi, le podium gagne une SKILLCARD du pack du moment. Pour un lot spécial,
          crée une SKILLCARD légendaire sans la mettre dans un pack.
        </p>
      )}
      {save.error && (
        <p role="alert" className="text-error-600 text-sm">
          {save.error.message}
        </p>
      )}
    </section>
  );
}
